import { createEnum } from '../helpers'

export const SUB_CHANNEL_VALUES = [
  'traditional_retail',
  'ngp_shops_bat_owned',
  'ngp_shops_3rd_party',
  'online_bat_owned',
  'drug_store',
] as const

export const SUB_CHANNELS = createEnum(SUB_CHANNEL_VALUES)
export type SubChannel = (typeof SUB_CHANNEL_VALUES)[number]

export const SUB_CHANNEL_LABEL_KEYS: Record<SubChannel, string> = {
  traditional_retail: 'subChannels.traditionalRetail',
  ngp_shops_bat_owned: 'subChannels.ngpShopsBatOwned',
  ngp_shops_3rd_party: 'subChannels.ngpShops3rdParty',
  online_bat_owned: 'subChannels.onlineBatOwned',
  drug_store: 'subChannels.drugStore',
}

export const PRICING_SUB_CHANNEL_CODES = [
  'GR - Grocery Stores',
  'VB - NGP Shops owned by BAT',
  'VO - NGP Shops owned by 3rd Parties',
  'OB - Online owned by BAT',
  'DR - Drug Store',
] as const
export type PricingSubChannelCode = (typeof PRICING_SUB_CHANNEL_CODES)[number]

const PRICING_SUB_CHANNEL_CODE_TO_VALUE: Record<PricingSubChannelCode, SubChannel> = {
  'GR - Grocery Stores': 'traditional_retail',
  'VB - NGP Shops owned by BAT': 'ngp_shops_bat_owned',
  'VO - NGP Shops owned by 3rd Parties': 'ngp_shops_3rd_party',
  'OB - Online owned by BAT': 'online_bat_owned',
  'DR - Drug Store': 'drug_store',
}

export const getPricingSubChannelValue = (subChannelName: PricingSubChannelCode): SubChannel => {
  return PRICING_SUB_CHANNEL_CODE_TO_VALUE[subChannelName]
}

const FORMATTED_PRICING_SUB_CHANNEL: Record<SubChannel, string> = {
  traditional_retail: 'Traditional Retail',
  ngp_shops_bat_owned: 'NGP Shops owned by BAT',
  ngp_shops_3rd_party: 'NGP Shops owned by 3rd Parties',
  online_bat_owned: 'Online owned by BAT',
  drug_store: 'Drug Store',
}
export const getFormattedPricingSubChannel = (subChannel: SubChannel): string => {
  return FORMATTED_PRICING_SUB_CHANNEL[subChannel]
}

/** Sentinel emitted to BPC for anything with no analyzable pricing sub-channel (e.g. FMC). */
export const BPC_NO_ANALYSIS_CODE = 'NO_ANALYSIS'

/** The bare 2-letter BPC channel codes (the prefixes of {@link PRICING_SUB_CHANNEL_CODES}), plus the fallback. */
export type BpcChannelCode = 'GR' | 'VB' | 'VO' | 'OB' | 'DR' | typeof BPC_NO_ANALYSIS_CODE

const SUB_CHANNEL_TO_BPC_CODE: Record<SubChannel, BpcChannelCode> = {
  traditional_retail: 'GR',
  ngp_shops_bat_owned: 'VB',
  ngp_shops_3rd_party: 'VO',
  online_bat_owned: 'OB',
  drug_store: 'DR',
}

/**
 * The BPC channel code for a sub-channel, as emitted in the P&L per-channel (Domestic/CDF)
 * exports that feed SAP BPC. FMC products are imported as `traditional_retail` (which would map to
 * `GR`), but must not be analyzed in BPC, so they always resolve to `NO_ANALYSIS`. Any sub-channel
 * with no known code also falls back to `NO_ANALYSIS`.
 */
export const getBpcChannelCode = (
  subChannel: SubChannel,
  options: { isFmc: boolean },
): BpcChannelCode => {
  if (options.isFmc) return BPC_NO_ANALYSIS_CODE
  return SUB_CHANNEL_TO_BPC_CODE[subChannel] ?? BPC_NO_ANALYSIS_CODE
}

/** Case-, whitespace-, dash- and underscore-insensitive. */
const normalizeSubChannelInput = (input: string) =>
  input
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, ' ')

/**
 * Every accepted spelling → {@link PRICING_SUB_CHANNEL_CODES} code. For `'DR - Drug Store'`: full
 * code, BPC code (`'DR'`), description, display name, canonical value (`'drug_store'`). No alias is
 * shared across sub-channels, so none shadows another.
 */
const SUB_CHANNEL_ALIAS_TO_CODE: Map<string, PricingSubChannelCode> = (() => {
  const lookup = new Map<string, PricingSubChannelCode>()

  for (const code of PRICING_SUB_CHANNEL_CODES) {
    const value = getPricingSubChannelValue(code)
    const [bpcCode, ...descriptionParts] = code.split(' - ')

    const aliases = [
      code,
      bpcCode,
      descriptionParts.join(' - '),
      getFormattedPricingSubChannel(value),
      value,
    ]

    for (const alias of aliases) {
      if (alias) lookup.set(normalizeSubChannelInput(alias), code)
    }
  }

  return lookup
})()

/**
 * Any accepted spelling → its {@link PRICING_SUB_CHANNEL_CODES} code. `'DR'`, `'drug store'`,
 * `'drug_store'`, `'DR - Drug Store'` → `'DR - Drug Store'`. `undefined` if unknown.
 */
export const resolvePricingSubChannelCode = (input: string): PricingSubChannelCode | undefined =>
  SUB_CHANNEL_ALIAS_TO_CODE.get(normalizeSubChannelInput(input))

/**
 * Any accepted spelling → its canonical {@link SUB_CHANNEL_VALUES} value. `'DR'`, `'Drug Store'`,
 * `'DR - Drug Store'` → `'drug_store'`. `undefined` if unknown.
 */
export const resolveSubChannel = (input: string): SubChannel | undefined => {
  const code = resolvePricingSubChannelCode(input)
  return code ? getPricingSubChannelValue(code) : undefined
}

/**
 * Lenient {@link resolveSubChannel}: unknown input passes through as is. For imports that persist
 * whatever the file holds instead of rejecting the row.
 */
export const resolveSubChannelValue = (input: string): string => resolveSubChannel(input) ?? input
